import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ExpandMoreIcon, HeaderEditIcon, PersonIcon, SignOutIcon } from '../../../../../Assets/svg/CommonAppHeaderIcons';
import { checkIsSimulationMode } from '@src/utility/createStorage';
import { profileDropdownStyles } from './ProfileDropdown.styles';
import './ProfileDropdown.interactions.css';

type PersonaUser = {
  UserType?: string;
  UserRole?: string;
};

type CurrentUserRecord = {
  UserPersonas?: PersonaUser[];
};

type LoggedUser = {
  FirstName?: string;
  LastName?: string;
  EmailId?: string;
  MsId?: string;
  OptumId?: string;
};

const readJson = <T,>(key: string, fallback: T): T => {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const ProfileDropdown: React.FC = () => {
  const [openUserMenu, setOpenUserMenu] = useState(false);
  const [isUserRowHovered, setIsUserRowHovered] = useState(false);
  const [isUserRowPressed, setIsUserRowPressed] = useState(false);
  const [loggedUser, setLoggedUser] = useState<LoggedUser>({});
  const [isInternal, setIsInternal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSimulationMode, setIsSimulationMode] = useState(checkIsSimulationMode());
  const userRef = useRef<HTMLDivElement>(null);

  const syncSession = useCallback(() => {
    const parsedLoggedUser = readJson<LoggedUser>('loggedUser', {});
    const parsedCurrentUser = readJson<CurrentUserRecord[]>('currentUser', []);
    const internalUser = parsedCurrentUser?.[0]?.UserPersonas?.[0]?.UserType === 'Internal';
    const adminUser = parsedCurrentUser?.[0]?.UserPersonas?.some((item: PersonaUser) => item.UserRole === 'UHCI_Admin') || false;
    setLoggedUser(parsedLoggedUser || {});
    setIsInternal(internalUser);
    setIsAdmin(adminUser);
    setIsSimulationMode(checkIsSimulationMode());
  }, []);

  useEffect(() => {
    syncSession();
    const timer = window.setInterval(syncSession, 500);
    return () => window.clearInterval(timer);
  }, [syncSession]);

  const fullName = useMemo(() => {
    return ((loggedUser?.FirstName || '') + ' ' + (loggedUser?.LastName || '')).trim();
  }, [loggedUser?.FirstName, loggedUser?.LastName]);

  useEffect(() => {
    const onMouseDown = (event: MouseEvent) => {
      if (userRef.current && !userRef.current.contains(event.target as Node)) {
        setOpenUserMenu(false);
      }
    };

    if (openUserMenu) {
      document.addEventListener('mousedown', onMouseDown);
    }

    return () => {
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [openUserMenu]);

  const handleContentEditorClick = () => {
    window.dispatchEvent(
      new CustomEvent('reactNavigation', {
        detail: { url: '/content-editor', index: 0, subIndex: 0, selectedRowIndex: 0, selectedSubIndex: 0 }
      })
    );
    setOpenUserMenu(false);
  };

  const handleSignOut = () => {
    window.dispatchEvent(new CustomEvent('signout'));
    setOpenUserMenu(false);
  };

  return (
    <div style={profileDropdownStyles.userWrap} ref={userRef}>
      <div
        style={{
          ...profileDropdownStyles.userRow,
          background: isUserRowPressed ? '#c8cfd6' : (isUserRowHovered ? '#d7dce1' : 'transparent'),
          color: isUserRowHovered || isUserRowPressed ? '#2d2d39' : '#757588'
        }}
        className="common-app-header-user-row"
        onMouseEnter={() => setIsUserRowHovered(true)}
        onMouseLeave={() => {
          setIsUserRowHovered(false);
          setIsUserRowPressed(false);
        }}
        onMouseDown={() => setIsUserRowPressed(true)}
        onMouseUp={() => setIsUserRowPressed(false)}
        onClick={() => setOpenUserMenu((prev) => !prev)}
      >
        <span style={profileDropdownStyles.userIconWrap} aria-hidden="true">
          <PersonIcon />
        </span>
        <span style={profileDropdownStyles.userName}>{loggedUser?.FirstName || 'User'}</span>
        <span style={profileDropdownStyles.userIconWrap} aria-hidden="true">
          <ExpandMoreIcon />
        </span>
      </div>

      {openUserMenu && (
        <div style={profileDropdownStyles.userMenu}>
          <div style={profileDropdownStyles.userDetails}>
            <p style={profileDropdownStyles.profileName}>{fullName}</p>
            <p style={profileDropdownStyles.profileText}>{loggedUser?.EmailId || ''}</p>
            <p style={profileDropdownStyles.profileText}>
              {isInternal ? `MSID: ${loggedUser?.MsId || ''}` : `One Healthcare ID: ${loggedUser?.OptumId || ''}`}
            </p>
          </div>

          {isAdmin && !isSimulationMode && (
            <div style={profileDropdownStyles.dropdownRow} onClick={handleContentEditorClick}>
              <HeaderEditIcon />
              <span style={profileDropdownStyles.dropdownText}>Content Editor</span>
            </div>
          )}

          <div style={profileDropdownStyles.dropdownRow}>
            <button style={profileDropdownStyles.buttonReset} onClick={handleSignOut}>
              <SignOutIcon />
              <span style={profileDropdownStyles.dropdownText}>Sign Out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileDropdown;